import { useAuth } from "@/src/contexts/auth.context";
import { deleteEntityFiles } from "@/src/services/api/storage/storage.service";
import {
  ApiNewsCategoryWithCount,
  BlocksApiNews,
  DeleteNewsOptions,
} from "@/src/models/news.model";
import {
  NEWS_ALL_QUERY_KEY,
  NEWS_BY_CATEGORY_QUERY_KEY,
  NEWS_CATEGORIES_QUERY_KEY,
  NEWS_DRAFT_QUERY_KEY,
  NEWS_ITEM_QUERY_KEY,
  NEWS_SAVED_QUERY_KEY,
} from "@/src/query-keys/news.keys";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import gqlClient from "@/src/services/api/graphql.client";
import {
  DELETE_NEWS,
  GET_NEWS,
  GET_NEWS_ITEM,
  UPDATE_NEWS,
} from "@/src/services/api/news/news.query";
import { deleteNewsFeedPost } from "@/src/services/api/news/news-post.service";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
import { useGetUserProfileQuery } from "@/src/services/profile/profile.hooks";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const DEFAULT_AUTHOR_AVATAR = require("@/src/assets/images/profile.png");

const ALL_NEWS_PAGE_SIZE = 6;

const request = async (query: string, variables?: object) => {
  const client = await gqlClient();
  return client.request(query, variables);
};

const fetchBlocksNews = async (
  where: object,
  pageSize: number,
  pageNo = 1,
): Promise<{ items: BlocksApiNews[]; totalCount: number }> => {
  const data: any = await request(GET_NEWS, {
    where,
    order: [{ field: "CreatedDate", direction: "DESC" }],
    pageSize,
    pageNo,
  });

  return {
    items: data?.getnewss?.items ?? [],
    totalCount: data?.getnewss?.totalCount ?? 0,
  };
};

// v4 typed `where`. `contains` replaces v1's `{ $regex, $options: "i" }` — the
// gateway has no regex operator.
const buildWhere = (
  extra: object,
  search?: string,
  options?: { isDraft?: boolean; isPinned?: boolean; visibility?: string },
): object => {
  const where: Record<string, unknown> = { ...extra };
  if (options?.isDraft !== undefined) {
    where.isDraft = { eq: options.isDraft };
  }
  if (options?.isPinned !== undefined) {
    where.isPinned = { eq: options.isPinned };
  }
  if (options?.visibility !== undefined) {
    where.visibility = { eq: options.visibility };
  }
  if (search?.trim()) {
    where.Title = { contains: search.trim() };
  }
  return where;
};

// Unscoped news screens (the main News tab, not a group's own page) never
// pass a `groupId`, so `buildWhere`'s single `visibility: { eq }` used to hard
// -code "Public" — a Group-visibility article never matched and never showed
// up outside the group it targeted, even for that group's own members. This
// mirrors `groupScopeWhere`/`filterPostVisibility` for posts: Public is always
// in, Group is in when the viewer belongs to one of the article's groups.
const newsVisibilityScope = (userGroups?: string[] | null): object =>
  userGroups?.length
    ? {
      or: [
        { visibility: { eq: "Public" } },
        { visibility: { eq: "Group" }, groupIds: { in: userGroups } },
      ],
    }
    : { visibility: { eq: "Public" } };

export const useNewsItem = (itemId?: string, enabled = true) =>
  useQuery({
    queryKey: [...NEWS_ITEM_QUERY_KEY, itemId ?? ""],
    queryFn: async () => {
      const client = await gqlClient();
      const data: any = await client.request(GET_NEWS_ITEM, {
        where: { ItemId: { eq: itemId } },
      });
      return data?.getnewss?.items?.[0] ?? null;
    },
    enabled: enabled && !!itemId,
  });

export const useAllNews = (search?: string, groupId?: string) => {
  const { userGroups } = useAuth();

  return useInfiniteQuery({
    queryKey: [
      ...NEWS_ALL_QUERY_KEY,
      groupId ?? "public",
      groupId ? "" : (userGroups ?? []).join(","),
      search ?? "",
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchBlocksNews(
        groupId
          ? buildWhere(
            { groupIds: { eq: groupId } },
            search,
            { isDraft: false, visibility: "Group" },
          )
          : buildWhere(newsVisibilityScope(userGroups), search, {
            isDraft: false,
          }),
        ALL_NEWS_PAGE_SIZE,
        pageParam as number,
      ),
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((t, p) => t + p.items.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
  });
};

export const usePinnedNews = (groupId?: string) => {
  const { userGroups } = useAuth();

  return useQuery({
    queryKey: [
      ...NEWS_ALL_QUERY_KEY,
      "pinned",
      groupId ?? "public",
      groupId ? "" : (userGroups ?? []).join(","),
    ],
    queryFn: () =>
      fetchBlocksNews(
        groupId
          ? buildWhere(
            { groupIds: { eq: groupId } },
            undefined,
            { isDraft: false, isPinned: true, visibility: "Group" },
          )
          : buildWhere(newsVisibilityScope(userGroups), undefined, {
            isDraft: false,
            isPinned: true,
          }),
        100,
      ).then((result) => result.items),
    staleTime: 5 * 60 * 1000,
  });
};

export const useTogglePinNews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      isPinned,
    }: {
      itemId: string;
      isPinned: boolean;
    }) => {
      await request(UPDATE_NEWS, {
        itemId,
        input: { isPinned },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NEWS_ALL_QUERY_KEY });
    },
  });
};

export const useNewsByCategory = (categoryId: string, search?: string) =>
  useQuery({
    queryKey: [...NEWS_BY_CATEGORY_QUERY_KEY, categoryId, search ?? ""],
    queryFn: () =>
      fetchBlocksNews(
        buildWhere({ categoryIds: { in: [categoryId] } }, search, {
          isDraft: false,
        }),
        100,
      ).then((result) => result.items),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });

export const useNewsCategoriesQuery = () => {
  const { data } = useGetCategoriesQuery({
    tag: "news",
    pageSize: 100,
  });

  const rawCategories = data?.items ?? [];

  return useQuery({
    queryKey: [
      ...NEWS_CATEGORIES_QUERY_KEY,
      rawCategories.map((c) => c.ItemId).join(","),
    ],
    queryFn: async (): Promise<ApiNewsCategoryWithCount[]> => {
      const { items: allNews } = await fetchBlocksNews(
        buildWhere({}, undefined, { isDraft: false }),
        1000,
      );

      const categoryMap = new Map<string, string>(
        rawCategories.map((c) => [String(c.ItemId), String(c.categoryName)]),
      );

      const articleCountByCategoryId = new Map<
        string,
        ApiNewsCategoryWithCount
      >();

      for (const newsItem of allNews) {
        for (const categoryId of newsItem.categoryIds ?? []) {
          const key = String(categoryId);
          const name = categoryMap.get(key) ?? key;

          if (articleCountByCategoryId.has(key)) {
            articleCountByCategoryId.get(key)!.articleCount += 1;
          } else {
            articleCountByCategoryId.set(key, {
              categoryID: key,
              name,
              articleCount: 1,
            });
          }
        }
      }

      return Array.from(articleCountByCategoryId.values());
    },
    enabled: rawCategories.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useNewsSaveState = (search?: string) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { data: profile } = useGetUserProfileQuery(userId);

  const savedArticleIds: string[] = Array.isArray(profile?.newsSaved)
    ? profile.newsSaved
    : [];

  const { data: savedNews = [], isLoading } = useQuery({
    queryKey: [...NEWS_SAVED_QUERY_KEY, userId, search ?? ""],
    queryFn: async () => {
      if (!savedArticleIds.length) return [];

      const result = await fetchBlocksNews(
        { ItemId: { in: savedArticleIds } },
        savedArticleIds.length,
      );

      return result.items;
    },
    enabled: !!userId && !!profile,
  });

  const filtered = search?.trim()
    ? savedNews.filter(
      (item) =>
        item?.Title?.toLowerCase().includes(search.toLowerCase()) ||
        item?.Content?.toLowerCase().includes(search.toLowerCase()),
    )
    : savedNews;

  return {
    savedNews: filtered,
    isSaved: (id: string) => savedArticleIds.includes(id),
    isLoadingSavedNews: isLoading,
    search,
  };
};

/**
 * Whether the current user has opened a given article, backed by `newsRead`
 * on their `UserProfile` row (parallel to `newsSaved`). Missing/undefined
 * `newsRead` — e.g. an existing profile row from before this field existed —
 * reads as "not read" rather than crashing.
 */
export const useNewsReadState = () => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { data: profile } = useGetUserProfileQuery(userId);

  const readArticleIds: string[] = Array.isArray(profile?.newsRead)
    ? profile.newsRead
    : [];

  return {
    isRead: (id: string) => readArticleIds.includes(id),
  };
};

export const useDraftNews = (search?: string) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { data: profile } = useGetUserProfileQuery(userId);

  const draftIds = Array.isArray(profile?.newsDraftIds)
    ? profile.newsDraftIds
    : [];

  return useQuery({
    queryKey: [...NEWS_DRAFT_QUERY_KEY, userId, draftIds, search ?? ""],
    queryFn: async () => {
      if (!draftIds.length) return [];

      const result = await fetchBlocksNews(
        buildWhere({ ItemId: { in: draftIds } }, search, { isDraft: true }),
        draftIds.length,
      );

      return result.items;
    },
    enabled: !!userId && draftIds.length > 0,
  });
};

export const useDeleteNews = ({
  categoryId,
  onSuccess,
}: DeleteNewsOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * Accepts either a bare id (existing call sites) or `{itemId, entity}`. When
     * the entity is supplied, its uploaded attachments are deleted from Blocks
     * Storage too — otherwise they are orphaned there forever.
     */
    mutationFn: async (input: string | { itemId: string; entity?: unknown }) => {
      const { itemId, entity } =
        typeof input === "string" ? { itemId: input, entity: undefined } : input;
      await request(DELETE_NEWS, { itemId });
      await deleteEntityFiles(entity);
      // The article's feed mirror outlives it otherwise: a post linking to an
      // article that no longer exists. Best-effort — the article is already
      // gone, so a failure here must not surface as a failed delete.
      try {
        await deleteNewsFeedPost(itemId);
      } catch (error) {
        console.warn("news mirror: delete failed", error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NEWS_ALL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_BY_CATEGORY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_CATEGORIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_SAVED_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_DRAFT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [POSTS_QUERY_KEY] });

      if (categoryId) {
        queryClient.invalidateQueries({
          queryKey: [...NEWS_BY_CATEGORY_QUERY_KEY, categoryId],
        });
      }

      onSuccess?.();
    },
  });
};
